import React, {ReactNode} from "react";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	FormDocument,
	FormsFormat,
	FormsImport,
	Functions,
	Information,
	MetadataFormsFormat,
	Security,
	Settings,
	SignatureEditProfile,
	ToolboxFormsForms
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {InputSwitch} from "primereact/inputswitch";
import {Base64} from "js-base64";
import ToolbarFunction, {ToolbarFunctionProperties, ToolbarFunctionState} from "./ToolbarFunction";
import {FormLayerGlobals} from "../pdf/form/FormLayerInterface";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import PortalDocument from "../../document/PortalDocument";
import MimeType from "../../document/mimetype/MimeType";
import PortalDialogSecurity, {Intention} from "../../dialog/PortalDialogSecurity";
import PortalDialogType from "../../dialog/PortalDialogType";
import MimeTypeFilter from "../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../document/mimetype/MimeTypes";
import Translate from "../../../shared/translation/Translate";
import FormsEditFunctionFields from "../../dialog/field/forms/FormsEditFunctionFields";
import FormValues from "../pdf/form/model/FormValues";
import PortalDocumentManager from "../../document/PortalDocumentManager";
import Message from "../../../shared/log/Message";
import {MessageType} from "../../../shared/log/MessageInterface";
import DocumentSettings from "../../document/DocumentSettings";
import Parameter from "../../dialog/parameter/Parameter";
import PortalDialogDefault from "../../dialog/parameter/PortalDialogDefault";
import {Button} from "primereact/button";
import Immutable from "../../../shared/immutable/Immutable";

export interface FormsEditToolbarFunctionProperties extends ToolbarFunctionProperties<ToolboxFormsForms>,
	FormLayerGlobals {
}

export class FormsEditToolbarFunctionDefinition extends ToolbarFunction<ToolboxFormsForms,
	FormsEditToolbarFunctionProperties, ToolbarFunctionState<ToolboxFormsForms>> {
	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.editForms, Functions.getEditFormsDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.EDIT_FORMS, signatureEditProfile, Intention.fillingInForms, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}

			if (FunctionHelper.applyValue(
				selectedDocument.metadata?.form?.field, FormDocument.getFieldDefault()
			).length === 0) {
				return false;
			}

			let settings: DocumentSettings = selectedDocument.getViewerSettings();
			if (!FunctionHelper.exists(settings.formInfo)) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		if (event.key.toLowerCase() === "escape" && !this.state.showPasswordDialog) {
			await this.cancel();
		}
	}

	public renderDialog(): ReactNode {
		return (
			<>
				{this.props.visible &&
					<div className="toolbar-function toolbar-forms-edit-function">
						{this.renderContents()}
						{this.renderButtons()}
					</div>
				}
			</>
		);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxFormsForms | undefined = this.state.parameter?.data;

		return (
			<div className="p-col p-grid" hidden={!this.state.fields?.contains(FormsEditFunctionFields.FLATTEN)}>
				<div className="switch-container">
					<InputSwitch inputId="control-flatten"
								 checked={FunctionHelper.applyValue(
									 parameter?._import?.flatten, FormsImport.getFlattenDefault()
								 )}
								 onChange={(e) => this.onChange(
									 FormsEditFunctionFields.FLATTEN, e.value
								 )}/>
					<label className="switch-label" htmlFor="control-flatten">
						<Translate {...this.props} data="edit_forms.flatten" namespace="dialog"/>
					</label>
				</div>
			</div>
		);
	}

	protected onChange(field: FormsEditFunctionFields, value: any): void {
		let parameter: ToolboxFormsForms = this.state.parameter!.data;

		switch (field) {
			case FormsEditFunctionFields.FLATTEN:
				parameter._import!.flatten = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	private async reset(): Promise<void> {
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let selected of selection) {
			let documentSettings: DocumentSettings = selected.getSettings();

			let formValues: FormValues = FormValues.fromMetadata(
				selected.metadata?.form, documentSettings?.formInfo
			);

			this.props.context.getPage().setState({
				formValues: Immutable.from(formValues.toJson())
			});
		}
	}

	protected renderHeader(): ReactNode {
		return null;
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canFillInForm, Security.getCanFillInFormDefault()
		);
	}

	/**
	 * Render the dialog buttons
	 */
	protected renderButtons(): ReactNode {
		return (
			<div className="toolbar-buttons p-col">
				<Button label={Translate.value("buttons.submit")} disabled={!this.isValid()}
						icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
						className="primary-button" onClick={() => this.submit()}/>
				<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
						onClick={() => this.cancel()} className="dialog-button"/>
				<Button label={Translate.value("buttons.reset")} icon="icon icon-rotate-270 small"
						disabled={this.state.submitted}
						onClick={() => {
							this.props.context.getPage().setState({
								showConfirmDialog: true,
								confirmDialogMessage: Translate.value("edit_forms.reset", {ns: "dialog"}),
								confirmDialogSubmit: this.reset.bind(this)
							});
						}} className="dialog-button"/>
			</div>
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ToolboxFormsForms | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		this.setState({
			parameter: this.state.parameter
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let formValues: FormValues = FormValues.fromJson(
			FunctionHelper.applyValue(this.props.formValues?.get(), {})
		);
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			let parameter: ToolboxFormsForms = this.state.parameter!.data!;

			try {
				parameter._import!.data!.value = Base64.encode(formValues.toXml(
					FunctionHelper.applyValue(
						portalDocument.metadata?.form?.formType, FormDocument.getFormTypeDefault()
					)
				));

				switch (FunctionHelper.applyValue(
					portalDocument.metadata?.form?.formType, FormDocument.getFormTypeDefault()
				)) {
					case MetadataFormsFormat.StaticXFA:
						parameter._import!.format = FormsFormat.Xdp;
						break;
					case MetadataFormsFormat.AcroForm:
						parameter._import!.format = FormsFormat.Xml;
						break;
				}

				let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
					forms: parameter.toJson()
				});

				let operationData: Array<BaseToolbox> = [toolboxOperation];

				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					showMessage: false,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as Settings)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.forms.import", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
			await DocumentSettings.resetMouseFunction(this.props);
			return;
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.EDIT_FORMS;
	}

	protected getDefaultParameter(): Parameter<ToolboxFormsForms> {
		return PortalDialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const FormsEditToolbarFunction = React.memo(FormsEditToolbarFunctionDefinition,
	(
		prevProps: Readonly<FormsEditToolbarFunctionProperties>,
		nextProps: Readonly<FormsEditToolbarFunctionProperties>
	) => {
		if (!nextProps.visible && !prevProps.visible && prevProps.visible !== nextProps.visible) {
			return true
		}

		return false;
	}
);

export default FormsEditToolbarFunction;